<template>
  <UPageSection
    v-if="isCarousel"
    :dir="dir"
    :title="resolvedTitle"
    :description="resolvedSubtitle"
    :ui="{
      root: 'min-w-0 overflow-x-clip',
      container: 'w-full max-w-full min-w-0 !gap-0 py-10 sm:py-14 lg:grid-cols-1 lg:py-10',
      title: 'max-w-full text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl text-center',
      description: 'mx-auto mt-4 max-w-3xl text-lg text-gray-500 dark:text-gray-400 text-center',
    }"
  >
    <div class="min-w-0 w-full">
      <ClientOnly v-if="resolvedPosts.length">
        <div class="block-blog__swiper my-8 min-w-0 w-full sm:my-12">
          <div
            v-if="showNavigation"
            class="mb-8 flex items-center gap-2"
            :class="dir === 'rtl' ? 'justify-end' : 'justify-start'"
          >
            <button
              type="button"
              class="block-blog__nav-btn block-blog__nav-prev border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navPrevLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-lucide-chevron-right' : 'i-lucide-chevron-left'"
                class="size-4"
              />
            </button>
            <button
              type="button"
              class="block-blog__nav-btn block-blog__nav-next border-gray-200 bg-white hover:bg-slate-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800"
              :aria-label="navNextLabel"
            >
              <UIcon
                :name="dir === 'rtl' ? 'i-lucide-chevron-left' : 'i-lucide-chevron-right'"
                class="size-4"
              />
            </button>
          </div>

          <Swiper
            :modules="swiperModules"
            :dir="dir"
            :slides-per-view="slidesPerView"
            :space-between="spaceBetween"
            :breakpoints="swiperBreakpoints"
            :navigation="navigationOptions"
            :pagination="paginationOptions"
            :autoplay="autoplayOptions"
            :loop="canLoop"
            class="equal-h-swiper block-blog-swiper overflow-hidden"
          >
            <SwiperSlide
              v-for="(post, index) in resolvedPosts"
              :key="index"
              class="h-auto py-2"
            >
              <UPageCard
                class="flex h-full min-h-120 flex-col sm:min-h-134"
                :title="post.title"
                :ui="{
                  title: 'text-xl leading-7 line-clamp-2 min-h-14',
                  description: 'min-h-18',
                  header: 'w-full',
                  wrapper: 'flex flex-1 flex-col',
                  footer: 'mt-auto pt-4',
                }"
              >
                <template v-if="post.image" #header>
                  <img
                    :src="post.image"
                    :alt="post.title"
                    class="mb-6 aspect-video w-full rounded-xl object-cover sm:mb-8"
                  >
                </template>

                <template #description>
                  <span class="line-clamp-3 min-h-18 text-base leading-6 text-gray-600 dark:text-gray-300">
                    {{ post.description }}
                  </span>
                </template>

                <template #footer>
                  <UButton
                    :label="ctaLabel"
                    :to="builderPreview ? undefined : (post.to || '#')"
                    color="primary"
                    variant="soft"
                    block
                    :trailing-icon="dir === 'rtl' ? 'i-lucide-chevron-left' : 'i-lucide-arrow-right'"
                    @click="builderPreview && post.to ? handleNavClick(post.to) : undefined"
                  />
                </template>
              </UPageCard>
            </SwiperSlide>
          </Swiper>
        </div>
        <template #fallback>
          <div class="grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <UPageCard
              v-for="(post, index) in resolvedPosts"
              :key="index"
              :title="post.title"
              :description="post.description"
            />
          </div>
        </template>
      </ClientOnly>
      <div
        v-else-if="loadingLivePosts"
        class="grid animate-pulse items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3"
        :class="isCarousel ? 'my-8 sm:my-12' : 'mt-10'"
        aria-label="در حال بارگذاری مقاله‌ها"
        aria-busy="true"
      >
        <div
          v-for="index in 3"
          :key="index"
          class="overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900/50"
        >
          <div class="aspect-16/10 rounded-xl bg-gray-100 dark:bg-gray-800" />
          <div class="mt-5 h-4 w-1/3 rounded bg-gray-100 dark:bg-gray-800" />
          <div class="mt-4 h-5 w-5/6 rounded bg-gray-100 dark:bg-gray-800" />
          <div class="mt-3 h-4 w-full rounded bg-gray-100 dark:bg-gray-800" />
          <div class="mt-2 h-4 w-2/3 rounded bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
      <p
        v-else
        class="text-center text-sm text-muted"
      >
        {{ emptyLabel }}
      </p>
    </div>
  </UPageSection>

  <section
    v-else
    class="py-16 md:py-24"
    :dir="dir"
  >
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <header
        v-if="resolvedTitle || resolvedSubtitle"
        class="mx-auto max-w-3xl text-center"
      >
        <h2
          v-if="resolvedTitle"
          class="text-3xl font-bold tracking-tight text-gray-900 dark:text-white md:text-4xl"
        >
          {{ resolvedTitle }}
        </h2>
        <p
          v-if="resolvedSubtitle"
          class="mt-3 text-base text-gray-600 dark:text-gray-400 md:text-lg"
        >
          {{ resolvedSubtitle }}
        </p>
      </header>

      <div
        v-if="resolvedPosts.length"
        class="mt-10 grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3"
      >
        <article
          v-for="(post, idx) in resolvedPosts"
          :key="idx"
          class="group flex h-full flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white transition hover:border-primary-500/40 hover:shadow-lg dark:border-gray-800 dark:bg-gray-900/50"
        >
          <button
            v-if="builderPreview"
            type="button"
            class="block aspect-16/10 w-full overflow-hidden bg-gray-100 text-start dark:bg-gray-800"
            :aria-label="post.title"
            @click="handleNavClick(post.to)"
          >
            <img
              v-if="post.image"
              :src="post.image"
              :alt="post.title"
              class="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            >
            <div
              v-else
              class="flex h-full w-full items-center justify-center text-gray-300 dark:text-gray-600"
            >
              <UIcon
                name="i-heroicons-photo"
                class="size-12"
              />
            </div>
          </button>
          <NuxtLink
            v-else
            :to="post.to"
            class="block aspect-16/10 w-full overflow-hidden bg-gray-100 dark:bg-gray-800"
            :aria-label="post.title"
          >
            <img
              v-if="post.image"
              :src="post.image"
              :alt="post.title"
              class="h-full w-full object-cover transition duration-300 group-hover:scale-105"
            >
            <div
              v-else
              class="flex h-full w-full items-center justify-center text-gray-300 dark:text-gray-600"
            >
              <UIcon
                name="i-heroicons-photo"
                class="size-12"
              />
            </div>
          </NuxtLink>

          <div class="flex flex-1 flex-col p-5">
            <div class="mb-2 flex items-center gap-2 text-xs">
              <UBadge
                v-if="post.badge"
                :label="post.badge"
                color="primary"
                variant="soft"
                size="sm"
              />
              <time
                v-if="post.date"
                :datetime="post.date"
                class="text-gray-500 dark:text-gray-400"
              >
                {{ formatDate(post.date) }}
              </time>
            </div>
            <h3 class="line-clamp-2 min-h-14 text-lg font-semibold leading-7 text-gray-900 dark:text-white">
              <button
                v-if="builderPreview"
                type="button"
                class="text-start hover:text-primary-600 dark:hover:text-primary-400"
                @click="handleNavClick(post.to)"
              >
                {{ post.title }}
              </button>
              <NuxtLink
                v-else
                :to="post.to"
                class="hover:text-primary-600 dark:hover:text-primary-400"
              >
                {{ post.title }}
              </NuxtLink>
            </h3>
            <p class="mt-2 line-clamp-3 min-h-18 flex-1 text-sm leading-6 text-gray-600 dark:text-gray-400">
              {{ post.description }}
            </p>
          </div>
        </article>
      </div>

      <div
        v-else-if="loadingLivePosts"
        class="mt-10 grid animate-pulse items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3"
        aria-label="در حال بارگذاری مقاله‌ها"
        aria-busy="true"
      >
        <div
          v-for="index in 3"
          :key="index"
          class="overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 dark:border-gray-800 dark:bg-gray-900/50"
        >
          <div class="aspect-16/10 rounded-xl bg-gray-100 dark:bg-gray-800" />
          <div class="mt-5 h-4 w-1/3 rounded bg-gray-100 dark:bg-gray-800" />
          <div class="mt-4 h-5 w-5/6 rounded bg-gray-100 dark:bg-gray-800" />
          <div class="mt-3 h-4 w-full rounded bg-gray-100 dark:bg-gray-800" />
          <div class="mt-2 h-4 w-2/3 rounded bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>

      <div
        v-else
        class="mt-10 rounded-2xl border border-dashed border-gray-200 px-6 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400"
      >
        {{ emptyLabel }}
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'

const dir = useDir()
const pageLocale = useState<string>('page-locale', () => 'fa')

type LocalizedString = string | Record<string, string>

type PostInput = {
  title?: LocalizedString
  description?: LocalizedString
  date?: string
  image?: string
  badge?: LocalizedString
  to?: string
}

const props = withDefaults(
  defineProps<{
    title?: LocalizedString
    subtitle?: LocalizedString
    posts?: PostInput[]
    layout?: 'grid' | 'list' | 'carousel'
    ctaLabel?: LocalizedString
    source?: 'latest' | 'category' | 'pinned'
    limit?: number
    categorySlug?: string
    showSearch?: boolean
    autoplay?: boolean
    autoplayDelay?: number
    loop?: boolean
    builderPreview?: boolean
    previewDevice?: 'desktop' | 'mobile'
    onNavigate?: (to: string) => void
  }>(),
  {
    posts: () => [],
    layout: 'grid',
    source: 'latest',
    limit: 6,
    autoplay: true,
    autoplayDelay: 5000,
    loop: true,
    builderPreview: false,
  },
)

function resolveVal(value: unknown, loc: string): any {
  if (value == null) return ''
  if (Array.isArray(value)) return value.map((item) => resolveVal(item, loc))
  if (typeof value === 'object') {
    const rec = value as Record<string, unknown>
    if (rec[loc] !== undefined) return rec[loc]
    return Object.fromEntries(
      Object.entries(rec).map(([k, v]) => [k, resolveVal(v, loc)]),
    )
  }
  return value
}

function localized(value: LocalizedString | undefined): string {
  if (!value) return ''
  if (typeof value === 'string') return value
  return String(resolveVal(value, pageLocale.value)) || ''
}

const isCarousel = computed(() => props.layout === 'carousel')
const resolvedTitle = computed(() => localized(props.title))
const resolvedSubtitle = computed(() => localized(props.subtitle))
const ctaLabel = computed(() =>
  localized(props.ctaLabel)
  || (pageLocale.value === 'fa' ? 'مطالعه مقاله' : 'Read article'),
)

const route = useRoute()
const livePosts = ref<Array<{ title: string; description: string; date: string; image: string; badge: string; to: string }>>([])
const loadingLivePosts = ref(!props.builderPreview)

async function loadLivePosts() {
  if (props.builderPreview) {
    loadingLivePosts.value = false
    return
  }
  loadingLivePosts.value = true
  try {
    const res = await $fetch<{ items: Array<{ title: string; excerpt: string; publishedAt: string | null; coverUrl?: string | null; category?: { title: string } | null; slug: string }> }>('/api/blog/posts', {
      query: {
        category: props.source === 'category' ? props.categorySlug : (typeof route.query.category === 'string' ? route.query.category : undefined),
        tag: typeof route.query.tag === 'string' ? route.query.tag : undefined,
        q: props.showSearch && typeof route.query.q === 'string' ? route.query.q : undefined,
        limit: props.limit || 6,
      },
    })
    livePosts.value = res.items.map((post) => ({
      title: post.title,
      description: post.excerpt,
      date: post.publishedAt || '',
      image: post.coverUrl || '',
      badge: post.category?.title || '',
      to: `/blog/${encodeURIComponent(post.slug)}`,
    }))
  }
  catch {
    livePosts.value = []
  }
  finally {
    loadingLivePosts.value = false
  }
}

onMounted(() => {
  void loadLivePosts()
})

const fallbackPosts = computed(() =>
  (props.posts ?? []).map((post) => ({
    title: localized(post.title),
    description: localized(post.description),
    date: post.date ?? '',
    image: post.image ?? '',
    badge: localized(post.badge),
    to: post.to ?? '#',
  })),
)

const resolvedPosts = computed(() => props.builderPreview ? fallbackPosts.value : livePosts.value)

const emptyLabel = computed(() =>
  pageLocale.value === 'fa' ? 'هنوز پستی اضافه نشده است.' : 'No posts added yet.',
)

const swiperModules = [Pagination, Navigation, Autoplay]
const itemCount = computed(() => resolvedPosts.value.length)

const spaceBetween = computed(() => {
  if (props.previewDevice === 'mobile') return 16
  if (props.previewDevice === 'desktop') return 24
  return 24
})

const slidesPerView = computed(() => {
  if (props.previewDevice === 'mobile') return 1
  if (props.previewDevice === 'desktop') return Math.min(2, Math.max(1, itemCount.value))
  return 1
})

const swiperBreakpoints = computed(() => {
  if (props.previewDevice) return undefined
  const max = Math.max(1, itemCount.value)
  return {
    0: { slidesPerView: 1, spaceBetween: 16 },
    640: { slidesPerView: Math.min(2, max), spaceBetween: 24 },
    1024: { slidesPerView: Math.min(3, max), spaceBetween: 32 },
  }
})

const showNavigation = computed(() => {
  if (props.previewDevice === 'mobile') return itemCount.value > 1
  if (props.previewDevice === 'desktop') return itemCount.value > 2
  return itemCount.value > 1
})

const navigationOptions = computed(() => {
  if (!showNavigation.value) return false
  return {
    prevEl: '.block-blog__nav-prev',
    nextEl: '.block-blog__nav-next',
  }
})

const navPrevLabel = computed(() =>
  pageLocale.value === 'fa' ? 'اسلاید قبلی' : 'Previous slide',
)

const navNextLabel = computed(() =>
  pageLocale.value === 'fa' ? 'اسلاید بعدی' : 'Next slide',
)

const paginationOptions = computed(() => ({
  clickable: true,
  dynamicBullets: true,
}))

const autoplayDelay = computed(() => {
  const value = Number(props.autoplayDelay)
  if (!Number.isFinite(value) || value < 1000) return 5000
  return Math.min(60000, Math.round(value))
})

const canLoop = computed(() => Boolean(props.loop) && itemCount.value > 1)

const autoplayOptions = computed(() => {
  if (!props.autoplay || itemCount.value < 2) return false
  return {
    delay: autoplayDelay.value,
    disableOnInteraction: true,
    pauseOnMouseEnter: true,
    stopOnLastSlide: !props.loop,
  }
})

function formatDate(date: string): string {
  if (!date) return ''
  try {
    return new Date(date).toLocaleDateString(pageLocale.value === 'fa' ? 'fa-IR' : pageLocale.value, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return date
  }
}

function handleNavClick(to: string | undefined) {
  if (!to) return
  if (props.builderPreview && props.onNavigate) props.onNavigate(to)
}
</script>

<style scoped>
.block-blog-swiper :deep(.swiper-pagination) {
  position: relative !important;
  bottom: auto !important;
  margin-top: 1rem;
  width: 100%;
}

.block-blog-swiper :deep(.swiper-pagination-bullet-active) {
  background-color: var(--ui-primary);
}

.block-blog__nav-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 9999px;
  border-width: 1px;
  border-style: solid;
  color: var(--ui-primary);
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease, opacity 0.15s ease;
}

.block-blog__nav-btn.swiper-button-disabled {
  opacity: 0.35;
  cursor: not-allowed;
}

.block-blog__nav-btn::after {
  display: none;
}

.block-blog__swiper :deep(.block-blog__nav-btn.swiper-button-prev),
.block-blog__swiper :deep(.block-blog__nav-btn.swiper-button-next) {
  position: static;
  inset: auto;
  margin: 0;
}
</style>
